import { NgModule, importProvidersFrom } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { featureFlagGuard } from '@shared/services/feature-flag.guard';

import { environment } from '../environments/environment';
import { ForceChangePasswordLeftComponent } from './pages/force-change-password/force-change-password-left/force-change-password-left.component';
import { ForceChangePasswordComponent } from './pages/force-change-password/force-change-password.component';
import { ForgotPasswordLeftComponent } from './pages/forgot-password/forgot-password-left/forgot-password-left.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { LoginLeftComponent } from './pages/login/login-left/login-left.component';
import { LoginRightComponent } from './pages/login/login-right.component';
import { ClassMarkupComponent } from './pages/price-setting/class-markup/class-markup.component';
import { StackPricingViewComponent } from './pages/price-setting/stack-pricing/stack-pricing-view/stack-pricing-view.component';
import { MonthlyPartnerProfitSharingListComponent } from './pages/profit-sharing/monthly-partner-profit-sharing-list/monthly-partner-profit-sharing-list.component';
import { ProfitSharingViewComponent } from './pages/profit-sharing/profit-sharing-view/profit-sharing-view.component';
import { ShelfListComponent } from './pages/shelf/shelf-list/shelf-list.component';
import { ShelfRequestListComponent } from './pages/shelf/shelf-request-list/shelf-request-list.component';
import { ShelfRequestComponent } from './pages/shelf/shelf-request/shelf-request.component';
import { ViewShelfComponent } from './pages/shelf/view-shelf/view-shelf.component';
import { StockInformationComponent } from './pages/stock/stock-information/stock-information.component';
import { StoreOperationPermission } from './pages/store-operation/store-operation-permission';
import { ShelfReplenishmentPhotoEffects } from './pages/store-operation/store/shelf-replenishment-photo.effects';
import { shelfReplenishmentPhotoReducer } from './pages/store-operation/store/shelf-replenishment-photo.reducers';
import { StoreStockRecallPermissionService } from './pages/store-stock-recall/store-stock-recall-permission';
import { CreateStockRecallEffect } from './pages/store-stock-recall/store/create-store-stock-recall/create-stock-recall.effect';
import { createStoreStockRecallReducer } from './pages/store-stock-recall/store/create-store-stock-recall/create-stock-recall.reducer';
import { StoreStockRecallListEffect } from './pages/store-stock-recall/store/store-stock-recall-list/store-stock-recall-list.effect';
import { storeStockRecallListReducer } from './pages/store-stock-recall/store/store-stock-recall-list/store-stock-recall-list.reducer';
import { UserCreateComponent } from './pages/user/user-create/user-create.component';
import { UserListComponent } from './pages/user/user-list/user-list.component';
import { LoginComponent } from './shared/layouts';
import { AdminLayoutComponent } from './shared/layouts/admin/admin-layout.component';
import { LoginLayoutComponent } from './shared/layouts/login-layout/login-layout.component';
import {
  grManagePermissions,
  grViewPermissions,
  poManagePermissions,
  poViewPermissions,
  prApprovePermissions,
  prManagePermissions,
  prViewPermissions
} from './shared/permissions/permissions';
import { AuthGuardService } from './shared/services';

export const appRoutes: Routes = [
  {
    path: '',
    component: LoginComponent,
    children: [
      {
        path: '',
        component: LoginRightComponent
      },
      {
        path: '',
        outlet: 'leftsection',
        component: LoginLeftComponent
      }
    ],
    pathMatch: 'full'
  },
  {
    path: '',
    component: LoginLayoutComponent,
    children: [
      {
        path: 'forgot-password',
        children: [
          {
            path: '',
            component: ForgotPasswordComponent
          },
          {
            path: '',
            outlet: 'leftsection',
            component: ForgotPasswordLeftComponent
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: LoginLayoutComponent,
    children: [
      {
        path: 'force-change-password',
        canActivate: [AuthGuardService],
        children: [
          {
            path: '',
            component: ForceChangePasswordComponent
          },
          {
            path: '',
            outlet: 'leftsection',
            component: ForceChangePasswordLeftComponent
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'dashboard',
        canActivate: [AuthGuardService],
        data: {
          title: 'HOME',
          group: 'HOME',
          icon: 'icon-home',
          active: true,
          order: 1,
          isShowOnMenu: true
        },
        loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule)
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'merchant-store',
        data: {
          title: 'MERCHANT_STORE',
          group: 'STORE',
          icon: 'icon-store',
          active: false,
          order: 2,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'merchant',
            canActivate: [AuthGuardService],
            data: {
              title: 'MERCHANT',
              isShowOnMenu: true,
              subgroup: 'MERCHANT',
              permissions: [
                'merchant_v',
                'merchant_vs',
                'merchant_key',
                'merchant_new_m',
                'merchant_new_req_v',
                'merchant_edit_m',
                'merchant_edit_req_v',
                'merchant_order_m',
                'merchant_app'
              ]
            },
            loadChildren: () => import('./pages/merchant/merchant.module').then(m => m.MerchantModule)
          },
          {
            path: 'store',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE',
              isShowOnMenu: true,
              subgroup: 'STORE',
              permissions: [
                'store_new_m',
                'store_new_req_v',
                'store_v',
                'store_eq',
                'store_locksale',
                'store_terminate',
                'store_edit_m',
                'store_app',
                'store_edit_req_v'
              ]
            },
            loadChildren: () => import('./pages/store/store.module').then(m => m.TDStoreModule)
          },
          {
            path: 'alcohol-sale-restrict-date',
            canActivate: [AuthGuardService],
            data: {
              title: 'ALCOHOL_SALE_RESTRICT_DATE',
              group: 'STORE',
              icon: 'icon-store',
              isShowOnMenu: true,
              permissions: ['alc_restrict_v', 'alc_restrict_m']
            },
            loadChildren: () =>
              import('./pages/alcohol-sale-restrict-date/alcohol-sale-restrict-date.routes').then(
                m => m.alcoholRestrictDateRoutes
              )
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'store-assortment',
        data: {
          title: 'STORE_ASSORTMENT',
          group: 'STORE',
          icon: 'icon-store-assortment',
          active: false,
          order: 3,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'store-assortment',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE_ASSORTMENT',
              isShowOnMenu: true,
              subgroup: 'STORE_ASSORTMENT',
              permissions: [
                'store_assortment_v',
                'store_assortment_m',
                'store_assortment_app',
                'store_assortment_req_v',
                'price_tag_v'
              ]
            },
            loadChildren: () =>
              import('./pages/store-assortment/store-assortment.module').then(m => m.StoreAssortmentModule)
          },
          {
            path: 'hold-assortment',
            canActivate: [AuthGuardService],
            data: {
              title: 'HOLD_ASSORTMENT',
              isShowOnMenu: true,
              subgroup: 'HOLD_ASSORTMENT',
              permissions: ['hold_assort_v', 'hold_assort_m', 'hold_assort_app']
            },
            loadChildren: () =>
              import('./pages/permanent-hold/permanent-hold.module').then(m => m.PermanentHoldModule)
          },
          {
            path: 'store-assortment-recommendation',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE_ASSORTMENT_RECOMMENDATION',
              isShowOnMenu: true,
              subgroup: 'STORE_ASSORTMENT_RECOMMENDATION',
              permissions: ['store_assort_recom_v']
            },
            loadChildren: () =>
              import('./pages/store-assortment-recommendation/store-assortment-recommendation.module').then(
                m => m.StoreAssortmentRecommendationModule
              )
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'store-operation',
        canActivate: [AuthGuardService],
        data: {
          title: 'STORE_OPERATION',
          group: 'STORE',
          icon: 'icon-operation',
          active: false,
          isShowOnMenu: true
        },
        providers: [StoreOperationPermission],
        children: [
          {
            path: 'shelf-replenishment-photo',
            canActivate: [AuthGuardService],
            data: {
              title: 'SHELF_REPLENISHMENT_PHOTO',
              isShowOnMenu: true,
              subgroup: 'SHELF_REPLENISHMENT',
              permissions: ['shelf_replen_rev_m', 'shelf_replen_rev_v']
            },
            providers: [
              importProvidersFrom(
                StoreModule.forFeature('shelfReplenishmentPhoto', shelfReplenishmentPhotoReducer),
                EffectsModule.forFeature([ShelfReplenishmentPhotoEffects])
              )
            ],
            loadChildren: () =>
              import('./pages/store-operation/shelf-replenishment-photo-routing').then(mod => mod.ROUTES)
          },
          {
            path: 'reset-expired-promotion-tag',
            canActivate: [AuthGuardService],
            data: {
              title: 'RESET_EXPIRED_PROMOTION_TAG',
              isShowOnMenu: true,
              subgroup: 'RESET_EXPIRED_PROMOTION_TAG',
              permissions: ['reset_exp_pro_tag_m', 'reset_exp_pro_tag_v']
            },
            loadChildren: () =>
              import('./pages/store-operation/reset-expired-promotion-tag-routing').then(mod => mod.ROUTES)
          },
          {
            path: 'hq-approval-code-portal',
            canActivate: [AuthGuardService],
            data: {
              title: 'HQ_APPROVAL_CODE_PORTAL',
              isShowOnMenu: true,
              subgroup: 'HQ_APPROVAL_CODE_PORTAL',
              permissions: ['hqapp_rf_highvalue_v', 'hqapp_qrpay_v', 'hqapp_rf_edc_v', 'hqapp_rf_no_ref_v']
            },
            loadChildren: () =>
              import('./pages/hq-approval-code-portal/hq-approval-code-portal-routing').then(module => module.ROUTES)
          },
          {
            path: 'focus-sales-product',
            canActivate: [AuthGuardService],
            data: {
              title: 'FOCUS_SALES_PRODUCT',
              isShowOnMenu: true,
              subgroup: 'FOCUS_SALES_PRODUCT',
              permissions: ['focus_prod_m', 'focus_prod_v']
            },
            loadChildren: () => import('./pages/store-operation/focus-sales-product-routing').then(mod => mod.ROUTES)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'task-assignment',
        data: {
          title: 'STORE_COMMUNICATION',
          group: 'STORE',
          icon: 'icon-task',
          active: false,
          order: 4,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'task-request',
            canActivate: [AuthGuardService],
            data: {
              title: 'TASK',
              isShowOnMenu: true,
              subgroup: 'TASK',
              permissions: ['task_m', 'task_req_v', 'task_v', 'task_req_app']
            },
            loadChildren: () => import('./pages/task/task.module').then(m => m.TaskModule)
          },
          {
            path: 'task-response',
            canActivate: [AuthGuardService],
            data: {
              title: 'RESPONSE',
              isShowOnMenu: true,
              subgroup: 'TASK',
              permissions: ['resp_v', 'resp_app']
            },
            loadChildren: () => import('./pages/task-response/task-response.module').then(m => m.TaskResponseModule)
          },
          {
            path: 'urgent-announcement',
            canActivate: [AuthGuardService],
            data: {
              title: 'URGENT_ANNOUNCEMENT',
              isShowOnMenu: true,
              subgroup: 'URGENT_ANNOUNCEMENT',
              permissions: ['ur_annouce_m', 'ur_annouce_v']
            },
            loadChildren: () =>
              import('./pages/urgent-announcement/urgent-announcement-routing').then(mod => mod.ROUTES)
          },
          {
            path: 'work-instruction',
            canActivate: [AuthGuardService],
            data: {
              title: 'WORK_INSTRUCTION',
              isShowOnMenu: true,
              subgroup: 'WORK_INSTRUCTION',
              permissions: [
                'wi_m',
                'wi_v',
                'work_schedule_m',
                'work_schedule_v',
                'wi_store_group_m',
                'wi_store_group_v'
              ]
            },
            loadChildren: () =>
              import('./pages/work-instruction/work-instruction.module').then(m => m.WorkInstructionModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'stock-count',
        data: {
          title: 'STOCK_COUNT',
          group: 'STORE',
          icon: 'icon-stock-count',
          active: false,
          order: 4,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'stock-count',
            canActivate: [AuthGuardService],
            data: {
              title: 'STOCK_COUNT',
              subgroup: 'STOCK_COUNT',
              isRegex: false,
              isShowOnMenu: true,
              permissions: ['stock_count_m', 'stock_count_v']
            },
            loadChildren: () => import('./pages/stock-count/stock-count.module').then(m => m.StockCountModule)
          },
          {
            path: 'store-available-adjustment',
            data: {
              title: 'STORE_AVAILABLE_ADJUSTMENT',
              isShowOnMenu: environment.prefixCompany === 'CJX',
              subgroup: 'STORE_AVAILABLE_ADJUSTMENT',
              isRegex: false,
              permissions: ['store_avai_adj_app', 'store_avai_adj_req_v']
            },
            loadChildren: () =>
              import('./pages/store-available-adjustment/store-available-adjustment.routes').then(
                m => m.storeAvailableAdjustmentRoutes
              )
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'membership',
        data: {
          title: 'MEMBERSHIP',
          group: 'STORE',
          icon: 'icon-membership',
          active: false,
          order: 5,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'member',
            canActivate: [AuthGuardService],
            data: {
              title: 'MEMBER',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'MEMBERSHIP',
              isRegex: false,
              permissions: ['mem_v']
            },
            loadChildren: () => import('./pages/member/member.module').then(m => m.MemberModule)
          },
          {
            path: 'exclude-item',
            canActivate: [AuthGuardService],
            data: {
              title: 'MEMBER_EXCLUDE_ITEM',
              isShowOnMenu: true,
              subgroup: 'MEMBER_EXCLUDE_ITEM',
              isRegex: false,
              permissions: ['exc_item_m', 'exc_item_v']
            },
            loadChildren: () =>
              import('./pages/member-exclude-item/member-exclude-item.module').then(m => m.MemberExcludeItemModule)
          },
          {
            path: 'reward-catalog',
            canActivate: [AuthGuardService],
            data: {
              title: 'REWARD_CATALOG',
              isShowOnMenu: true,
              subgroup: 'REWARD_CATALOG',
              permissions: ['rwc_m', 'rwc_app', 'rwc_req_v', 'rwc_v']
            },
            loadChildren: () =>
              import('./pages/reward-catalog/reward-catalog.module').then(m => m.RewardCatalogModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'order',
        data: {
          title: 'ORDER',
          group: 'STORE',
          icon: 'icon-order',
          active: false,
          order: 6,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'order',
            canActivate: [AuthGuardService],
            data: {
              title: 'ORDER',
              isShowOnMenu: true,
              subgroup: 'ORDER',
              permissions: [
                'so_v',
                'or_v',
                'so_firstlot_m',
                'so_special_m',
                'so_app',
                'or_fl_ast_m',
                'or_fl_ast_app',
                'or_st_equip_m',
                'or_fl_inv_m',
                'or_fl_inv_app',
                'or_fl_qty_inv_m',
                'pe_order_m',
                'me_order_m'
              ]
            },
            loadChildren: () => import('./pages/order/order.module').then(m => m.OrderModule)
          },
          {
            path: 'group-order-list',
            canActivate: [AuthGuardService],
            data: {
              title: 'GROUP_ORDER',
              isShowOnMenu: true,
              subgroup: 'GROUP_ORDER',
              permissions: ['go_v']
            },
            loadChildren: () => import('./pages/group-order/group-order.module').then(m => m.GroupOrderModule)
          },
          {
            path: 'delivery-order-list',
            canActivate: [AuthGuardService],
            data: {
              title: 'DELIVERY_ORDER_SHIPMENT',
              isShowOnMenu: true,
              subgroup: 'DELIVERY_ORDER_SHIPMENT',
              isRegex: true,
              permissions: [
                '^do_logis_v_[a-zA-Z]{2}[0-9]{1,3}$',
                '^do_logis_m_[a-zA-Z]{2}[0-9]{1,3}$',
                '^do_v_[a-zA-Z]{2}[0-9]{1,3}$',
                '^do_m_[a-zA-Z]{2}[0-9]{1,3}$'
              ]
            },
            loadChildren: () =>
              import('./pages/delivery-order/delivery-order-list/delivery-order-list.module').then(
                m => m.DeliveryOrderListModule
              )
          },
          {
            path: 'shipment-list',
            canActivate: [AuthGuardService],
            data: {
              subgroup: 'DELIVERY_ORDER_SHIPMENT',
              isRegex: true,
              permissions: [
                '^do_logis_v_[a-zA-Z]{2}[0-9]{1,3}$',
                '^do_logis_m_[a-zA-Z]{2}[0-9]{1,3}$',
                '^do_m_[a-zA-Z]{2}[0-9]{1,3}$'
              ]
            },
            loadChildren: () =>
              import('./pages/shipment/shipment-list/shipment-list.module').then(m => m.ShipmentListModule)
          },
          {
            path: 'receive-order-list',
            canActivate: [AuthGuardService],
            data: {
              title: 'RECEIVE_ORDER',
              isShowOnMenu: true,
              subgroup: 'RECEIVE_ORDER',
              permissions: ['ro_v', 'cr_m']
            },
            loadChildren: () => import('./pages/receive-order/receive-order.module').then(m => m.ReceiveOrderModule)
          },
          {
            path: 'claim-request',
            canActivate: [AuthGuardService],
            data: {
              title: 'CLAIM',
              isShowOnMenu: true,
              subgroup: 'CLAIM',
              isRegex: false,
              permissions: ['cr_v', 'cr_app', 'cr_m', 'cr_v', 'cr_app']
            },
            loadChildren: () => import('./pages/claim-request/claim-request.module').then(m => m.ClaimRequestModule)
          },
          {
            path: 'hold-order',
            canActivate: [AuthGuardService],
            data: {
              title: 'HOLD_ORDER',
              isShowOnMenu: true,
              subgroup: 'HOLD_ORDER',
              isRegex: false,
              permissions: ['ho_com_m', 'ho_req_v', 'ho_v']
            },
            loadChildren: () => import('./pages/hold-order/hold-order.module').then(m => m.HoldOrderModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'billing',
        data: {
          title: 'BILLING',
          group: 'STORE',
          icon: 'icon-bill',
          active: false,
          order: 7,
          isShowOnMenu: environment.prefixCompany !== 'CJX'
        },
        children: [
          {
            path: 'profit-sharing',
            canActivate: [AuthGuardService],
            data: {
              title: 'PROFIT_SHARING',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'PROFIT_SHARING',
              permissions: ['profitsharing_v', 'profitsharing_m']
            },
            loadChildren: () =>
              import('./pages/profit-sharing/profit-sharing.module').then(m => m.ProfitSharingModule)
          },
          {
            path: 'profit-sharing-view',
            component: ProfitSharingViewComponent,
            canActivate: [AuthGuardService],
            data: {
              permissions: ['profitsharing_v', 'profitsharing_m']
            }
          },
          {
            path: 'refund',
            canActivate: [AuthGuardService],
            data: {
              title: 'REFUND',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              permissions: ['rf_m', 'rf_app', 'rf_req_v', 'rf_v', 'rf_cf']
            },
            loadChildren: () => import('./pages/refund/refund.module').then(m => m.RefundModule)
          },
          {
            path: 'monthly-partner-profit-sharing-list',
            component: MonthlyPartnerProfitSharingListComponent,
            canActivate: [AuthGuardService],
            data: {
              title: 'MONTHLY_PROFIT_SHARING',
              subgroup: 'PROFIT_SHARING',
              permissions: ['profitsharing_m']
            }
          },
          {
            path: 'late-payment-notice',
            canActivate: [AuthGuardService],
            data: {
              title: 'LATE_PAYMENT_NOTICE',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'LATE_PAYMENT_NOTICE',
              isRegex: false,
              permissions: ['late_notice_m', 'late_notice_v']
            },
            loadChildren: () =>
              import('./pages/late-payment-notice/late-payment-notice.module').then(m => m.LatePaymentNoticeModule)
          },
          {
            path: 'pre-order-credit-term',
            canActivate: [AuthGuardService],
            data: {
              title: 'PRE_ORDER_CREDIT_TERM',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'PRE_ORDER_CREDIT_TERM',
              isRegex: false,
              permissions: ['sp_credit_m', 'sp_credit_v']
            },
            loadChildren: () =>
              import('./pages/pre-order-credit-term/pre-order-credit-term.module').then(
                m => m.PreOrderCreditTermModule
              )
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'products',
        data: {
          title: 'PRODUCT',
          group: 'ADMIN',
          icon: 'icon-product',
          active: false,
          order: 8,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'master-list',
            canActivate: [AuthGuardService],
            data: {
              title: 'PRODUCT_MASTER',
              isShowOnMenu: true,
              subgroup: 'PRODUCT_MASTER',
              permissions: ['cjproduct_m_v', 'cjproduct_nm_v', 'sup_product_m', 'sup_product_v']
            },
            loadChildren: () => import('./pages/products/products.module').then(m => m.ProductsModule)
          },
          {
            path: 'request-list',
            canActivate: [AuthGuardService],
            data: {
              subgroup: 'PRODUCT_ASSORTMENT',
              permissions: [
                'assort_inv_m',
                'assort_asset_m',
                'assort_sto_m',
                'assort_inv_app',
                'assort_asset_app',
                'assort_sto_app',
                'assort_inv_req_v',
                'assort_asset_req_v',
                'assort_sto_req_v'
              ]
            },
            loadChildren: () =>
              import('./pages/assortment-request/assortment-request.module').then(m => m.AssortmentRequestModule)
          },
          {
            path: 'product-list',
            canActivate: [AuthGuardService],
            data: {
              title: 'PRODUCT_ASSORTMENT',
              isShowOnMenu: true,
              subgroup: 'PRODUCT_ASSORTMENT',
              permissions: [
                'assort_inv_m',
                'assort_asset_m',
                'assort_sto_m',
                'assort_inv_app',
                'assort_asset_app',
                'assort_sto_app',
                'assort_inv_v',
                'assort_asset_v',
                'assort_sto_v',
                'assort_inv_req_v',
                'assort_asset_req_v',
                'assort_sto_req_v'
              ]
            },
            loadChildren: () =>
              import('./pages/assortment-product/assortment-product-list/assortment-product-list.module').then(
                m => m.AssortmentProductListModule
              )
          },
          {
            path: 'stack-pricing-edit',
            component: StackPricingViewComponent,
            canActivate: [AuthGuardService],
            data: {
              permissions: ['price_m_v', 'price_m_m', 'price_nm_v', 'price_nm_m']
            }
          },
          {
            path: 'class-markup',
            component: ClassMarkupComponent,
            canActivate: [AuthGuardService],
            data: {
              permissions: ['price_m_v', 'price_m_m', 'price_nm_v', 'price_nm_m']
            }
          },
          {
            path: 'shelf-list',
            component: ShelfListComponent,
            canActivate: [AuthGuardService],
            data: {
              title: 'SHELF',
              isShowOnMenu: true,
              subgroup: 'SHELF',
              permissions: ['shelf_v', 'shelf_m', 'shelf_app', 'shelf_firstlot_m']
            }
          },
          {
            path: 'shelf-request-list',
            component: ShelfRequestListComponent,
            canActivate: [AuthGuardService],
            data: {
              subgroup: 'SHELF',
              permissions: ['shelf_m', 'shelf_app', 'shelf_firstlot_m']
            }
          },
          {
            path: 'shelf-request',
            component: ShelfRequestComponent,
            canActivate: [AuthGuardService]
          },
          {
            path: 'view-shelf',
            component: ViewShelfComponent,
            canActivate: [AuthGuardService]
          },
          {
            path: 'pre-order',
            canActivate: [AuthGuardService],
            data: {
              title: 'PRE_ORDER',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'PRE_ORDER',
              permissions: ['pe_cat_m', 'pe_cat_v', 'pe_fls_m']
            },
            loadChildren: () => import('./pages/pre-order/pre-order.module').then(m => m.PreOrderModule)
          },

          {
            path: 'pim',
            data: {
              title: 'PRODUCT_INFORMATION_MANAGEMENT',
              isShowOnMenu: true,
              subgroup: 'PRODUCT_PIM',
              permissions: [
                'prod_inv_m',
                'prod_inv_req_v',
                'prod_inv_app',
                'prod_inv_v',
                'prod_asset_m',
                'prod_asset_req_v',
                'prod_asset_app',
                'prod_asset_v',
                'prod_sto_m',
                'prod_sto_req_v',
                'prod_sto_app',
                'prod_sto_v'
              ]
            },
            loadChildren: () => import('./pages/pim/pim-routing.module').then(m => m.PIM_ROUTES)
          },
          {
            path: 'special-retail-price',
            canActivate: [AuthGuardService],
            data: {
              title: 'SPECIAL_RETAIL_PRICE',
              isShowOnMenu: true,
              subgroup: 'SPECIAL_RETAIL_PRICE',
              permissions: ['special_rsp_v', 'special_rsp_m', 'special_rsp_req_v', 'special_rsp_app']
            },
            loadChildren: () =>
              import('./pages/special-retail-price/special-retail-price.routes').then(m => m.specialRetailPriceRoutes)
          },
          {
            path: 'not-for-sale',
            canActivate: [AuthGuardService],
            data: {
              title: 'NOT_FOR_SALE_ITEM',
              isShowOnMenu: true,
              subgroup: 'NOT_FOR_SALE_ITEM',
              isRegex: false,
              permissions: ['notsale_item_m', 'notsale_item_v'] // TODO: Update Permission
            },
            loadChildren: () =>
              import('./pages/not-for-sale-item/not-for-sale-item.module').then(m => m.NotForSaleItemModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'cafe',
        data: {
          title: 'CAFE',
          group: 'ADMIN',
          icon: 'icon-cafe',
          active: false,
          order: 8,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'cafe-catalog',
            canActivate: [AuthGuardService],
            data: {
              title: 'CAFE_CATALOG',
              isShowOnMenu: true,
              subgroup: 'CAFE_CATALOG',
              permissions: ['cafe_cat_m', 'cafe_cat_v']
            },
            loadChildren: () => import('./pages/cafe-catalog/cafe-catalog.module').then(m => m.CafeCatalogModule)
          },
          {
            path: 'cafe-topping',
            canActivate: [AuthGuardService],
            data: {
              title: 'CAFE_TOPPING',
              isShowOnMenu: true,
              subgroup: 'CAFE_TOPPING',
              permissions: ['cafe_top_m', 'cafe_top_v']
            },
            loadChildren: () => import('./pages/cafe-topping/cafe-topping.module').then(m => m.CafeToppingModule)
          },
          {
            path: 'cafe-menu-station-mapping',
            canActivate: [AuthGuardService],
            data: {
              title: 'CAFE_MENU_STATION_MAPPING',
              isShowOnMenu: true,
              subgroup: 'CAFE_MENU_STATION_MAPPING',
              permissions: ['cafe_sta_m', 'cafe_sta_v']
            },
            loadChildren: () =>
              import('./pages/cafe-menu-station-mapping/cafe-menu-station-mapping.module').then(
                m => m.CafeMenuStationMappingModule
              )
          },
          {
            path: 'cafe-menu-restriction',
            canActivate: [AuthGuardService],
            data: {
              title: 'CAFE_MENU_RESTRICTION',
              isShowOnMenu: true,
              subgroup: 'CAFE_MENU_RESTRICTION',
              permissions: ['cafe_restr_m', 'cafe_restr_v']
            },
            loadChildren: () =>
              import('./pages/cafe-menu-restriction/cafe-menu-restriction.module').then(
                m => m.CafeMenuRestrictionModule
              )
          },
          {
            path: 'cafe-advertisement',
            canActivate: [AuthGuardService],
            data: {
              title: 'CAFE_ADVERTISEMENT',
              isShowOnMenu: true,
              subgroup: 'CAFE_ADVERTISEMENT',
              permissions: ['cafe_ads_m', 'cafe_ads_v']
            },
            loadChildren: () =>
              import('./pages/cafe-advertisement/cafe-advertisement.module').then(m => m.CafeAdvertisementModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'shelf',
        data: {
          title: 'SHELF',
          group: 'ADMIN',
          icon: 'icon-shelf',
          active: false,
          order: 9,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'shelf-inventory',
            canActivate: [AuthGuardService],
            data: {
              title: 'SHELF_INVENTORY',
              isShowOnMenu: true,
              subgroup: 'SHELF_INVENTORY',
              permissions: ['sh_inv_rq_v', 'sh_inv_v', 'sh_inv_m', 'sh_inv_app']
            },
            loadChildren: () =>
              import('./pages/shelf/shelf-inventory/shelf-inventory.module').then(m => m.ShelfInventoryModule)
          },
          {
            path: 'shelf-fix-asset',
            canActivate: [AuthGuardService],
            data: {
              title: 'SHELF_FIX_ASSET',
              isShowOnMenu: true,
              subgroup: 'SHELF_FIX_ASSET',
              permissions: ['sh_ast_v', 'sh_ast_rq_v', 'sh_ast_m', 'sh_ast_app']
            },
            loadChildren: () =>
              import('./pages/shelf/shelf-fix-asset/shelf-fix-asset.module').then(m => m.ShelfFixAssetModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'campaign',
        data: {
          title: 'CAMPAIGN',
          group: 'ADMIN',
          icon: 'icon-marketing',
          active: false,
          order: 10,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'promotion',
            canActivate: [AuthGuardService],
            data: {
              title: 'PROMOTION',
              isShowOnMenu: true,
              subgroup: 'PROMOTION',
              permissions: ['promotion_m', 'promotion_v', 'promotion_req_v', 'promotion_app', 'promotion_image_m']
            },
            loadChildren: () => import('./pages/promotion-v2/promotion-v2.module').then(m => m.PromotionV2Module)
          },
          {
            path: 'voucher',
            canActivate: [AuthGuardService],
            data: {
              title: 'VOUCHER',
              isShowOnMenu: true,
              subgroup: 'VOUCHER',
              permissions: ['voucher_m', 'voucher_v', 'voucher_app', 'voucher_req_v', 'voucher_dl']
            },
            loadChildren: () => import('./pages/voucher/voucher.module').then(m => m.VoucherModule)
          },
          {
            path: 'receipt-coupon',
            canActivate: [AuthGuardService],
            data: {
              title: 'RECEIPT_COUPON',
              isShowOnMenu: true,
              subgroup: 'RECEIPT_COUPON',
              isRegex: false,
              permissions: ['rc_coupon_m', 'rc_coupon_app', 'rc_coupon_req_v', 'rc_coupon_v']
            },
            loadChildren: () =>
              import('./pages/receipt-coupon/receipt-coupon.module').then(m => m.ReceiptCouponModule)
          },
          {
            path: 'supplier-partner-coupon',
            canActivate: [AuthGuardService],
            data: {
              title: 'SUPPLIER_PARTNER_COUPON',
              isShowOnMenu: true,
              subgroup: 'SUPPLIER_PARTNER_COUPON',
              isRegex: false,
              permissions: ['sup_coupon_v', 'pwp_img_m', 'pwp_img_v']
            },
            loadChildren: () =>
              import('./pages/supplier-partner-coupon/supplier-partner-coupon.module').then(
                m => m.SupplierPartnerCouponModule
              )
          },

          {
            path: 'gift-with-purchase',
            canActivate: [AuthGuardService],
            data: {
              title: 'GIFT_WITH_PURCHASE',
              isShowOnMenu: true,
              subgroup: 'GIFT_WITH_PURCHASE',
              isRegex: false,
              permissions: ['gift_v', 'gift_m', 'gift_req_v', 'gift_app']
            },
            loadChildren: () =>
              import('./pages/gift-with-purchase/gift-with-purchase.module').then(m => m.GiftWithPurchaseModule)
          },
          {
            path: 'reward',
            canActivate: [AuthGuardService],
            data: {
              title: 'REWARD',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'REWARD',
              isRegex: false,
              permissions: ['rw_v', 'rw_m', 'rr_v', 'rw_app']
            },
            loadChildren: () => import('./pages/reward/reward.module').then(m => m.RewardModule)
          },
          {
            path: 'member-point',
            canActivate: [AuthGuardService],
            data: {
              title: 'MEMBER_POINT',
              isShowOnMenu: true,
              subgroup: 'MEMBER_POINT',
              permissions: ['point_earn_m', 'point_earn_app', 'point_earn_v', 'point_earn_req_v']
            },
            loadChildren: () => import('./pages/member-point/member-point.module').then(m => m.MemberPointModule)
          },
          {
            path: 'point-burn-setting',
            canActivate: [AuthGuardService],
            data: {
              title: 'POINT_BURN_SETTING',
              isShowOnMenu: true,
              subgroup: 'POINT_BURN_SETTING',
              isRegex: false,
              permissions: ['point_burn_m', 'point_burn_app', 'point_burn_v', 'point_burn_req_v']
            },
            loadChildren: () =>
              import('./pages/point-burn-setting/point-burn-setting.module').then(m => m.PointBurnSettingModule)
          },
          {
            path: 'online-coupon',
            canActivate: [AuthGuardService],
            data: {
              title: 'ONLINE_COUPON',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'ONLINE_COUPON',
              isRegex: false,
              permissions: ['oc_m', 'oc_v', 'oc_req_v', 'oc_app']
            },
            loadChildren: () => import('./pages/online-coupon/online-coupon.module').then(m => m.OnlineCouponModule)
          },
          {
            path: 'pos-advertisement',
            canActivate: [AuthGuardService],
            data: {
              title: 'POS_ADVERTISEMENT',
              isShowOnMenu: true,
              subgroup: 'POS_ADVERTISEMENT',
              permissions: ['pos_ads_m', 'pos_ads_v']
            },
            loadChildren: () =>
              import('./pages/pos-advertisement/pos-advertisement.module').then(m => m.PosAdvertisementModule)
          },
          {
            path: 'create-audience',
            data: {
              title: 'CREATE_AUDIENCE',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'CREATE_AUDIENCE',
              permissions: ['aud_m', 'aud_v']
            },
            loadChildren: () =>
              import('./pages/create-audience/create-audience.module').then(m => m.CreateAudienceModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'purchase',
        data: {
          title: 'PURCHASE',
          group: 'ADMIN',
          icon: 'icon-purchase',
          active: false,
          order: 11,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'purchase-request',
            canActivate: [AuthGuardService],
            data: {
              title: 'PR_PO_GR',
              isShowOnMenu: true,
              subgroup: 'PR_PO_GR',
              isRegex: true,
              permissions: [
                ...prViewPermissions,
                ...prManagePermissions,
                ...prApprovePermissions,
                ...poViewPermissions,
                ...poManagePermissions,
                ...grViewPermissions,
                ...grManagePermissions
              ]
            },
            loadChildren: () => import('./pages/purchase/purchase.module').then(m => m.PurchaseModule)
          },
          {
            path: 'purchase-condition',
            canActivate: [AuthGuardService],
            data: {
              title: 'PURCHASE_CONDITION',
              isShowOnMenu: true,
              subgroup: 'PURCHASE_CONDITION',
              isRegex: false,
              permissions: ['pc_req_m', 'pc_req_app', 'pc_req_v', 'pc_v']
            },
            loadChildren: () =>
              import('./pages/purchase-condition/purchase-condition.module').then(m => m.PurchaseConditionModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'stock',
        data: {
          title: 'STOCK',
          group: 'ADMIN',
          icon: 'icon-stock',
          active: false,
          order: 12,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'stock-information',
            canActivate: [AuthGuardService],
            data: {
              title: 'STOCK_INFORMATION',
              isShowOnMenu: true,
              subgroup: 'STOCK_INFORMATION',
              permissions: ['^stk_v_[a-zA-Z]{2}[0-9]{1,3}$', 'stk_v_sto', 'stk_v_all'],
              isRegex: true
            },
            loadChildren: () => import('./pages/stock/stock.module').then(m => m.StockModule)
          },
          {
            path: 'stock-information',
            component: StockInformationComponent,
            canActivate: [AuthGuardService],
            data: {
              permissions: []
            }
          },
          {
            path: 'manage-stock',
            canActivate: [AuthGuardService],
            data: {
              title: 'STOCK_ADJUSTMENT',
              isShowOnMenu: true,
              subgroup: 'STOCK_ADJUSTMENT',
              permissions: [
                'stkadj_m_sto',
                'stkadj_v_sto',
                'stkadj_app_sto',
                'stkadj_req_v_sto',
                '^stkadj_v_[a-zA-Z]{2}[0-9]{1,3}$',
                '^stkadj_m_[a-zA-Z]{2}[0-9]{1,3}$',
                '^stkadj_app_[a-zA-Z]{2}[0-9]{1,3}$',
                '^stkadj_req_v_[a-zA-Z]{2}[0-9]{1,3}$'
              ],
              isRegex: true
            },
            loadChildren: () => import('./pages/manage-stock/manage-stock.module').then(m => m.ManageStockModule)
          },
          {
            path: 'store-stock-planning',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE_STOCK_PLANNING',
              isShowOnMenu: true,
              subgroup: 'STORE_STOCK_PLANNING',
              permissions: ['^ssr_v_[a-zA-Z]{2}[0-9]{1,3}$'],
              isRegex: true
            },
            loadChildren: () =>
              import('./pages/store-stock-planning/store-stock-planning.module').then(m => m.StoreStockPlanningModule)
          },
          {
            path: 'store-stock-recall',
            canActivate: [AuthGuardService, featureFlagGuard],
            data: {
              title: 'STORE_STOCK_RECALL',
              isShowOnMenu: true,
              subgroup: 'STORE_STOCK_RECALL',
              permissions: ['recall_m', 'recall_v'],
              featureName: ['store-stock-recall.warehouse-recall-mgt']
            },
            providers: [
              importProvidersFrom(
                StoreModule.forFeature('storeStockRecallList', storeStockRecallListReducer),
                StoreModule.forFeature('creatStoreStockRecall', createStoreStockRecallReducer),
                EffectsModule.forFeature([StoreStockRecallListEffect, CreateStockRecallEffect])
              ),
              StoreStockRecallPermissionService
            ],
            loadChildren: () =>
              import('./pages/store-stock-recall/store-stock-recall-routing').then(mod => mod.ROUTES)
          },
          {
            path: 'stock-transfer',
            canActivate: [AuthGuardService],
            data: {
              title: 'STOCK_TRANSFER',
              isShowOnMenu: true,
              subgroup: 'STOCK_TRANSFER',
              permissions: ['tr_m', 'to_v', 'tr_v', 'tr_app', 'to_gr_v']
            },
            loadChildren: () =>
              import('./pages/stock-transfer/stock-transfer.module').then(m => m.StockTransferModule)
          },
          {
            path: 'return-to-warehouse',
            canActivate: [AuthGuardService],
            data: {
              title: 'RETURN-TO-WAREHOUSE',
              isShowOnMenu: true,
              subgroup: 'RETURN-TO-WAREHOUSE',
              permissions: ['rtw_m', 'rtw_r_v', 'rtw_v', 'rtw_gr_v', 'rtw_req_app'],
              isRegex: false
            },
            loadChildren: () =>
              import('./pages/return-to-warehouse/return-to-warehouse.module').then(m => m.ReturnToWarehouseModule)
          },
          {
            path: 'return-to-supplier',
            canActivate: [AuthGuardService],
            data: {
              title: 'RETURN_TO_SUPPLIER',
              isShowOnMenu: true,
              subgroup: 'RETURN_TO_SUPPLIER',
              permissions: ['return_to_sup_v'],
              isRegex: false
            },
            loadChildren: () =>
              import('./pages/return-to-supplier/return-to-supplier.routes').then(m => m.returnToSupplierRoutes)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'tote',
        data: {
          title: 'TOTE',
          group: 'ADMIN',
          icon: 'icon-tote',
          active: false,
          order: 12,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'tote-information',
            canActivate: [AuthGuardService],
            data: {
              title: 'TOTE_INFORMATION',
              isShowOnMenu: true,
              subgroup: 'TOTE_INFORMATION',
              isRegex: false,
              permissions: ['tote_info_v']
            },
            loadChildren: () =>
              import('./pages/tote-information/tote-information.module').then(m => m.ToteInformationModule)
          },
          {
            path: 'tote-adjustment',
            canActivate: [AuthGuardService, featureFlagGuard],
            data: {
              title: 'TOTE_ADJUSTMENT',
              isShowOnMenu: true,
              subgroup: 'TOTE_ADJUSTMENT',
              isRegex: false,
              permissions: ['tote_adjust_v', 'tote_adj_m'],
              featureName: ['tote-adjustment.store-stock-mgt']
            },
            loadChildren: () =>
              import('./pages/tote-adjustment/tote-adjustment.module').then(m => m.ToteAdjustmentModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'supplier',
        data: {
          title: 'SUPPLIER',
          group: 'ADMIN',
          icon: 'icon-supplier',
          active: false,
          order: 13,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'supplier',
            canActivate: [AuthGuardService],
            data: {
              title: 'SUPPLIER',
              isShowOnMenu: true,
              subgroup: 'SUPPLIER',
              permissions: ['supplier_m', 'supplier_v', 'supplier_set_m']
            },
            loadChildren: () => import('./pages/supplier/supplier.module').then(m => m.SupplierModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'workflow',
        data: {
          title: 'WORKFLOW',
          group: 'ADMIN',
          icon: 'icon-approval-workflow',
          active: false,
          order: 14,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'approval-workflow',
            canActivate: [AuthGuardService],
            data: {
              title: 'APPROVAL_WORKFLOW',
              isShowOnMenu: true,
              subgroup: 'APPROVAL_WORKFLOW',
              isRegex: false,
              permissions: ['app_workflow_m', 'app_workflow_v']
            },
            loadChildren: () =>
              import('./pages/approval-workflow/approval-workflow.routes').then(m => m.approvalWorkflowRoutes)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'user-management',
        data: {
          title: 'USER_MANAGEMENT',
          group: 'ADMIN',
          icon: 'icon-user',
          active: false,
          order: 15,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'user-list',
            component: UserListComponent,
            canActivate: [AuthGuardService],
            data: {
              title: 'USER',
              isShowOnMenu: true,
              subgroup: 'USERS',
              permissions: ['user_bo_m', 'user_bo_v']
            }
          },
          {
            path: 'regional-structure',
            canActivate: [AuthGuardService],
            data: {
              title: 'REGIONAL_STRUCTURE',
              isShowOnMenu: true,
              subgroup: 'REGIONAL_STRUCTURE',
              permissions: ['region_struc_m', 'region_struc_v']
            },
            loadChildren: () =>
              import('./pages/regional-structure/regional-structure.module').then(m => m.RegionalStructureModule)
          },
          {
            path: 'role-and-permission',
            canActivate: [AuthGuardService],
            data: {
              title: 'ROLE_PERMISSION',
              isShowOnMenu: true,
              subgroup: 'ROLE',
              permissions: ['role_m', 'role_v', 'permission_m', 'permission_v']
            },
            loadChildren: () => import('./pages/roles/roles.module').then(m => m.RolesModule)
          },
          {
            path: 'store-group',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE_GROUP',
              isShowOnMenu: true,
              subgroup: 'STORE_GROUP',
              permissions: ['store_group_m', 'store_group_v']
            },
            loadChildren: () => import('./pages/store-group/store-group.module').then(m => m.StoreGroupModule)
          },
          {
            path: 'store-consultant',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE_CONSULTANT',
              isShowOnMenu: true,
              subgroup: 'STORE_CONSULTANT',
              permissions: ['store_consult_m', 'store_consult_v']
            },
            loadChildren: () =>
              import('./pages/store-consultant/store-consultant.module').then(m => m.StoreConsultantModule)
          },
          {
            path: 'user-create',
            component: UserCreateComponent,
            canActivate: [AuthGuardService],
            data: {
              permissions: ['user_bo_m']
            }
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'setting',
        canActivate: [AuthGuardService],
        data: {
          title: 'SETTING',
          group: 'ADMIN',
          icon: 'icon-setting',
          active: false,
          order: 16,
          isShowOnMenu: true,
          permissions: [
            'shelf_replen_schedule_s',
            'shelf_replen_schedule_v',
            'shelf_replen_prior_s',
            'shelf_replen_prior_v',
            'stock_count_schedule_s',
            'stock_count_schedule_v',
            'sto_pick_unit_s',
            'sto_pick_unit_v',
            'material_req_type_m',
            'material_req_type_v',
            'petty_cash_event_m',
            'petty_cash_event_v',
            'petty_cash_default_m',
            'petty_cash_default_v',
            'hotkey_m',
            'hotkey_v',
            'sale_schedule_s',
            'sale_schedule_v',
            'store_exp_limit_m',
            'store_exp_limit_v',
            'sto_expense_type_m',
            'sto_expense_type_v',
            'prod_cat_fix_s',
            'prod_cat_fix_v',
            'prod_cat_inv_s',
            'prod_cat_inv_v',
            'product_wh_s',
            'product_wh_v',
            'wh_oper_s',
            'department_v',
            'department_m',
            'prod_brand_m',
            'prod_brand_v',
            'alternative_prod_m',
            'alternative_prod_v',
            'prod_cat_own_deli_m',
            'prod_cat_own_deli_v',
            'prod_attribute_m',
            'prod_attribute_v'
          ]
        },
        loadChildren: () => import('./pages/setting/setting.module').then(m => m.SettingModule)
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'reports',
        canActivate: [AuthGuardService],
        data: {
          title: 'REPORT',
          group: 'ADMIN',
          icon: 'icon-report',
          active: false,
          order: 16,
          isShowOnMenu: true,
          isRegex: true,
          permissions: ['^rep_.+_v$']
        },
        loadChildren: () => import('./pages/new-reports/new-report.module').then(m => m.NewReportModule)
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'customer360',
        canActivate: [AuthGuardService],
        data: {
          title: 'CUSTOMER_360',
          group: 'DATA_INSIGHT',
          icon: 'icon-customer-360',
          active: false,
          isShowOnMenu: false,
          order: 2,
          permissions: ['cus_360_m']
        },
        loadChildren: () => import('./pages/customer360/customer360.module').then(m => m.Customer360Module)
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'nest-dashboard',
        data: {
          title: 'DASHBOARD',
          group: 'DATA_INSIGHT',
          icon: 'icon-dashboard',
          active: false,
          order: 3,
          isShowOnMenu: environment.prefixCompany !== 'CJX'
        },
        children: [
          {
            path: 'flash-sale',
            canActivate: [AuthGuardService],
            data: {
              title: 'FLASH_SALE',
              isShowOnMenu: environment.prefixCompany !== 'CJX',
              subgroup: 'FLASH_SALE',
              permissions: ['fls_dash_v']
            },
            loadChildren: () =>
              import('./pages/flash-sale-dashboard/flash-sale-dashboard.module').then(m => m.FlashSaleDashboardModule)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'rtc-and-destroy',
        data: {
          title: 'RTC_AND_DESTROY',
          group: 'STORE',
          icon: 'icon-rtc-dt',
          active: false,
          order: 6,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'reduce-to-clear',
            canActivate: [AuthGuardService],
            data: {
              title: 'REDUCE_TO_CLEAR',
              isShowOnMenu: true,
              subgroup: 'REDUCE_TO_CLEAR',
              permissions: ['rtc_app', 'rtc_req_v', 'rtc_v']
            },
            loadChildren: () =>
              import('./pages/reduce-to-clear/reduce-to-clear.routes').then(m => m.reduceToClearRoutes)
          },
          {
            path: 'reduce-to-clear-rate',
            canActivate: [AuthGuardService],
            data: {
              title: 'REDUCE_TO_CLEAR_RATE',
              isShowOnMenu: true,
              subgroup: 'REDUCE_TO_CLEAR_RATE',
              permissions: ['rtc_rate_m', 'rtc_rate_v']
            },
            loadChildren: () =>
              import('./pages/reduce-to-clear-rate/reduce-to-clear-rate.module').then(m => m.ReduceToClearRateModule)
          },
          {
            path: 'destroy',
            canActivate: [AuthGuardService],
            data: {
              title: 'DESTROY',
              isShowOnMenu: true,
              subgroup: 'DESTROY',
              permissions: ['dt_v', 'dt_req_v', 'dt_req_app']
            },
            loadChildren: () => import('./pages/destroy/destroy-routing.module').then(m => m.routes)
          }
        ]
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'sale',
        data: {
          title: 'SALE_AND_EXPENSE',
          group: 'STORE',
          icon: 'icon-sale',
          active: false,
          order: 7,
          isShowOnMenu: true
        },
        children: [
          {
            path: 'cash-settlement',
            canActivate: [AuthGuardService],
            data: {
              title: 'CASH_SETTLEMENT',
              isShowOnMenu: true,
              subgroup: 'CASH_SETTLEMENT',
              permissions: ['cash_settle_v', 'cash_settle_cf']
            },
            loadChildren: () =>
              import('./pages/cash-settlement/cash-settlement.module').then(m => m.CashSettlementModule)
          },
          {
            path: 'refund-exception',
            canActivate: [AuthGuardService],
            data: {
              title: 'REFUND_EXCEPTION_MANAGEMENT',
              isShowOnMenu: true,
              subgroup: 'REFUND_EXCEPTION_MANAGEMENT',
              permissions: ['rf_except_v', 'rf_except_m', 'cancel_rf_except_m']
            },
            loadChildren: () => import('./pages/refund-exception/refund-exception-routing.module').then(m => m.routes)
          },
          {
            path: 'store-expense',
            canActivate: [AuthGuardService],
            data: {
              title: 'STORE_EXPENSE',
              isShowOnMenu: true,
              subgroup: 'STORE_EXPENSE',
              permissions: ['store_exp_req_v', 'store_exp_req_app']
            },
            loadChildren: () => import('./pages/store-expense/store-expense.module').then(m => m.StoreExpenseModule)
          },
          {
            path: 'monthly-sales-target',
            canActivate: [AuthGuardService, featureFlagGuard],
            data: {
              title: 'MONTHLY_SALES_TARGET',
              isShowOnMenu: true,
              subgroup: 'MONTHLY_SALES_TARGET',
              isRegex: false,
              permissions: ['sales_target_m', 'sales_target_v'],
              featureName: ['monthly-sales-target.display-store-goal']
            },
            loadChildren: () =>
              import('./pages/monthly-sales-target/monthly-sales-target.module').then(m => m.MonthlySalesTargetModule)
          }
        ]
      }
    ]
  },
  { path: '**', redirectTo: '/' }
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
